import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Info, Lock } from 'lucide-react';
import { LIFECYCLE_MESSAGES, formatLifecycleMessage, lifecycleBanner, type LifecycleBannerTone } from '@his/shared';
import { useAuth } from '@/lib/auth';
import { dateOnly } from '@/lib/utils';
import { ABOUT_SETTINGS_PATH } from '@/lib/updates-api';

const TONE: Record<LifecycleBannerTone, string> = {
  info: 'border-primary/30 bg-primary/10 text-primary',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  danger: 'border-destructive/30 bg-destructive/10 text-destructive',
};

const ACTION_PATH = { renew: ABOUT_SETTINGS_PATH, support: '/support', export: ABOUT_SETTINGS_PATH } as const;

export function LifecycleBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!user || user.isPlatformUser) return null;
  const banner = lifecycleBanner(user.lifecycle ?? null, { isAdmin: user.isSuperAdmin === true, now: new Date() });
  if (!banner) return null;
  const Icon = banner.tone === 'info' ? Info : banner.tone === 'danger' ? Lock : AlertTriangle;
  const text = formatLifecycleMessage(banner.key, banner.params, (iso) => dateOnly(iso));
  return (
    <div
      role={banner.tone === 'danger' ? 'alert' : 'status'}
      className={`flex flex-wrap items-center justify-center gap-2 border-b px-4 py-2 text-xs font-medium no-print ${TONE[banner.tone]}`}
    >
      <Icon className="h-3.5 w-3.5 flex-none" aria-hidden />
      <span>{text}</span>
      {banner.action && (
        <button type="button" className="underline underline-offset-2" onClick={() => navigate(ACTION_PATH[banner.action as keyof typeof ACTION_PATH])}>
          {LIFECYCLE_MESSAGES[`lifecycle.action.${banner.action}`]}
        </button>
      )}
    </div>
  );
}
